import { notification } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QuestionCircleOutlined } from '@ant-design/icons'

import { FileEntityType } from 'services/FileService'
import { ordersApi } from 'sections/LKSection/modules/OrderModule/api'
import { OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { Order } from 'sections/LKSection/modules/OrderModule/stores/entity/Order'
import { Pvz } from 'sections/LKSection/modules/PvzModule/stores/entity/Pvz'
import { PickPointFileItemType, PickPointFileType } from 'sections/LKSection/shared/PickPointSharedModule/types'
import { Margin } from 'components/ui'
import { Info } from 'components/ui/Info/Info'
import { UploadFile } from 'components/ui/UploadFile/UploadFile'
import { Checkbox, Divider, Flex, Image, Paragraph, Popover, Text } from 'components/uiKit'
import { ConvertedFile } from 'utils/helpers/files/fileUpload'
import { useResize } from 'utils/hooks/useResize'
import { colors, PHOTO_VALIDATE, VIDEO_VALIDATE, widths } from 'shared/constants/values'

import { PickPointFilesViewModel } from '../../../models/PickPointFilesViewModel'
import { urlForSecretImages } from '../data'

import buildingFacade from 'assets/img/lk-section/Order/buildingFacade.png'

const getKey = (key: string) => `${OrderKeys.filesStep}.${key}`

type PickPointFileItemProps = {
  uiStore: PickPointFilesViewModel
  fileType: PickPointFileType
  label: string
  description: string[] | string
  entity?: Order | Pvz | null
  isCreation?: boolean
  disabled?: boolean
  dataTestId?: string
  initialized?: boolean
}

export const PickPointFileItem = observer(
  ({
    uiStore,
    fileType,
    label,
    description,
    entity,
    dataTestId,
    isCreation = false,
    disabled = false,
    initialized = false,
  }: PickPointFileItemProps) => {
    const { t } = useTranslation()
    const { isMobile } = useResize()

    const [loading, setLoading] = useState(false)
    const [localContentData, setLocalContentData] = useState(uiStore[fileType]?.url)
    const [documentUrl, setDocumentUrl] = useState('')

    const isDocument =
      fileType === PickPointFileType.leaseDocument ||
      fileType === PickPointFileType.btiDocument ||
      fileType === PickPointFileType.leaseDocumentEditArea ||
      fileType === PickPointFileType.btiDocumentEditArea
    const isPhoto = fileType !== PickPointFileType.video && !isDocument
    const isVideo = fileType === PickPointFileType.video

    const content = isDocument && !isCreation ? documentUrl : localContentData
    const isOrder = entity instanceof Order
    const entityType = isOrder ? FileEntityType.pickPointOrder : FileEntityType.pickPoint

    useEffect(() => {
      const getDocumentsPhoto = async (url: string) => {
        try {
          const file = await ordersApi.getSecretImages(url)
          setDocumentUrl(file)
        } catch (error) {
          notification.error({ message: `${t(getKey('Ошибка отображения документов'))}. ${(error as Error).message}` })
        }
      }

      if (initialized && !documentUrl && uiStore[fileType].url.startsWith(urlForSecretImages)) {
        void getDocumentsPhoto(uiStore[fileType].url)
      }
    }, [initialized])

    const changeFileHandler = async (file: ConvertedFile) => {
      try {
        setLoading(true)

        setLocalContentData(file.content)

        if (isVideo) {
          const videoItem = { name: file.name, url: file.content, size: file.fileSize }

          if (entity?.id) {
            const res = await uiStore.uploadVideo(entity.id, [videoItem], entityType)
            uiStore.setVideoFile(res[0])
          } else {
            uiStore.setVideoFile(videoItem)
          }
        } else {
          const photoItem: PickPointFileItemType = {
            url: file.content,
            size: file.fileSize,
            type: fileType,
            isPublic: uiStore[fileType].isPublic,
          }

          if (entity?.id) {
            const res = await uiStore.uploadPhotos(entity.id, [photoItem], entityType, isDocument)
            uiStore.setPhotoData(fileType)(res[0])
          } else {
            uiStore.setPhotoData(fileType)({ url: file.content, size: file.fileSize })
          }

          if (isDocument) {
            setDocumentUrl(file.content)
          }
        }
      } finally {
        setLoading(false)
      }
    }

    const deleteButtonHandler = () => {
      setLocalContentData('')

      if (fileType === PickPointFileType.video) {
        uiStore.setVideoFile({ name: '', url: '', size: 0 })
      } else {
        uiStore.setPhotoData(fileType)({ url: '', size: 0 })

        if (isDocument) {
          setDocumentUrl('')
        }
      }
    }

    return (
      <Flex vertical>
        <Flex gap={24}>
          <UploadFile
            contentUrl={content}
            disabled={disabled}
            fileValidationOptions={isVideo ? VIDEO_VALIDATE : PHOTO_VALIDATE}
            hidePreview={isVideo}
            onChange={changeFileHandler}
            onDelete={deleteButtonHandler}
            dataTestId={dataTestId}
            isFileUploading={loading}
          />

          <Flex vertical>
            <Margin mb={8}>
              <Flex gap={8}>
                <Text strong>{t(getKey(label))}</Text>

                {fileType === PickPointFileType.nightlyGeneralPhoto && (
                  <Popover
                    trigger={'click'}
                    placement={isMobile ? 'bottom' : 'rightTop'}
                    content={
                      <Flex vertical style={{ maxWidth: widths.mobile }}>
                        <Paragraph>{t(getKey('Пример, как должно выглядеть'))}</Paragraph>

                        <Image src={buildingFacade} alt="Фасад здания" />
                      </Flex>
                    }
                  >
                    <QuestionCircleOutlined style={{ color: colors.gray }} />
                  </Popover>
                )}
              </Flex>
            </Margin>

            {Array.isArray(description) ? (
              description.map((item) => <Paragraph key={item}>{t(getKey(item))}</Paragraph>)
            ) : (
              <Paragraph>{t(getKey(description))}</Paragraph>
            )}

            {isPhoto && (
              <Checkbox
                type="checkbox"
                checked={uiStore[fileType].isPublic}
                onChange={(e) => uiStore.setPhotoIsPublic(fileType)(e.target.checked)}
                disabled={disabled}
                data-testid={`${dataTestId}Checkbox`}
              >
                {t(getKey('Опубликовать'))}
              </Checkbox>
            )}
          </Flex>
        </Flex>

        {isVideo && uiStore[fileType].url && <Info type="success">{t(getKey('Видео загружено'))}</Info>}

        {fileType !== PickPointFileType.video && <Divider />}
      </Flex>
    )
  },
)
